/**
 * Errori di meta-cloud-proxy tradotti per l'operatore, in italiano.
 *
 * Perche' esiste (27/09/2026): quando il proxy risponde con un codice HTTP di
 * errore, supabase.functions.invoke restituisce un errore senza `data` e il CRM
 * mostrava "meta-cloud-proxy invocation failed: Edge Function returned a
 * non-2xx status code". Il corpo della risposta (letto da error.context) ha
 * invece la forma:
 *   { ok: false, status, error: { code, message, canonical_code, user_message, retryable } }
 * oppure, per gli errori del proxy stesso (configurazione, accesso):
 *   { error: 'testo tecnico in inglese' }
 *
 * Qui si sceglie la frase da mostrare: mai testo tecnico inglese all'utente.
 * Modulo puro, testato in __tests__/cloudProxyError.test.ts.
 */

import { describeCloudError } from './cloudErrorMessages';

export const WINDOW_CLOSED_MESSAGE =
  "La finestra di 24 ore e' chiusa: per ora puoi inviare solo un modello approvato.";

/** Codici Meta che indicano la finestra di 24 ore chiusa. */
const WINDOW_CLOSED_CODES = new Set([131047]);

export interface ProxyErrorInfo {
  canonicalCode: string;
  userMessage: string;
  retryable: boolean;
  statusCode: number;
  rawCode: number | string | undefined;
}

function genericByStatus(status: number): string {
  if (status === 401 || status === 403) return 'Accesso non riuscito: esci e rientra nel CRM, poi riprova.';
  if (status === 404) return 'Numero WhatsApp non trovato o non attivo. Controlla le impostazioni di WhatsApp.';
  if (status === 429) return 'Troppe richieste in poco tempo: aspetta qualche secondo e riprova.';
  if (status === 503) return 'Non riesco a leggere le credenziali del numero WhatsApp. Riprova tra poco.';
  return 'WhatsApp non ha risposto correttamente. Riprova tra poco.';
}

/**
 * Frase e codici da un corpo di risposta del proxy (anche assente o illeggibile).
 * `httpStatus` e' lo stato HTTP della risposta, usato quando il corpo non basta.
 */
export function proxyErrorInfo(body: unknown, httpStatus?: number): ProxyErrorInfo {
  const b = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
  const status = Number(b.status) || httpStatus || 502;
  const err = b.error;
  if (err && typeof err === 'object') {
    const e = err as Record<string, unknown>;
    const rawCode = (e.code as number | string | undefined) ?? undefined;
    const canonical = typeof e.canonical_code === 'string' ? e.canonical_code : 'UNKNOWN';
    const n = Number(rawCode);
    const windowClosed = canonical === 'WINDOW_CLOSED' || WINDOW_CLOSED_CODES.has(n);
    let userMessage: string;
    if (windowClosed) userMessage = WINDOW_CLOSED_MESSAGE;
    else if (Number.isFinite(n) && describeCloudError(n) !== 'Invio non riuscito.') userMessage = describeCloudError(n);
    else if (typeof e.user_message === 'string' && e.user_message.trim()) userMessage = e.user_message.trim();
    else userMessage = genericByStatus(status);
    return {
      canonicalCode: windowClosed ? 'WINDOW_CLOSED' : canonical,
      userMessage,
      retryable: typeof e.retryable === 'boolean' ? e.retryable : false,
      statusCode: status,
      rawCode,
    };
  }
  return {
    canonicalCode: 'UNKNOWN',
    userMessage: genericByStatus(httpStatus || status),
    retryable: false,
    statusCode: httpStatus || status,
    rawCode: undefined,
  };
}

/** True se l'errore (MetaError o simile) dice che la finestra di 24 ore e' chiusa. */
export function isWindowClosedError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { canonicalCode?: unknown; rawCode?: unknown };
  return e.canonicalCode === 'WINDOW_CLOSED' || WINDOW_CLOSED_CODES.has(Number(e.rawCode));
}

// ─── Segnale "finestra chiusa" ───────────────────────────────────────
// Un invio respinto da Meta perche' la finestra e' chiusa lo segnala qui;
// useCloudCsw lo ascolta e fa proporre i modelli approvati come quando la
// finestra risulta chiusa dal database.

type Listener = (chatId: string) => void;
const listeners = new Set<Listener>();

export function signalWindowClosed(chatId: string | null | undefined): void {
  if (!chatId) return;
  for (const fn of listeners) {
    try { fn(chatId); } catch { /* un ascoltatore rotto non blocca gli altri */ }
  }
}

export function onWindowClosed(fn: Listener): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Da chiamare nel catch di un invio: se Meta ha detto "finestra chiusa", lo segnala. */
export function notifyIfWindowClosed(chatId: string | null | undefined, err: unknown): void {
  if (isWindowClosedError(err)) signalWindowClosed(chatId);
}
